import get from 'lodash/get';
import isString from 'lodash/isString';
import cookies from 'js-cookie';

class TCLFormHelper {
  constructor(schema) {
    this.schema = schema;
    this.sanitizeSchema(this.schema.formSchema);
  }

  get confirmationCopy() {
    return get(this.schema, 'tclSchema.extras.confirmationMessage');
  }

  get confirmationHeading() {
    return get(this.schema, 'tclSchema.extras.confirmationMessageHeader');
  }

  get errorCopy() {
    return get(this.schema, 'tclSchema.extras.failureMessage');
  }

  get errorHeading() {
    return get(this.schema, 'tclSchema.extras.failureMessageHeader');
  }

  get errorMessages() {
    return get(this.schema, 'formSchema.errorMessage');
  }

  field(path) {
    const name = path.replace(/^\/properties\//, '');
    const schema = get(this.formSchema, `properties.${name}`, null);
    const allOf = get(this.formSchema, 'allOf', []);
    const isConditionalField = allOf.find(
      ({ then: { properties } }) => !!properties[name.replace('ui:relevant:', '')]
    );

    if (isConditionalField) {
      return schema
        ? {
            name: name.replace('ui:relevant:', ''),
            schema: {
              properties: {},
              allOf,
            },
          }
        : null;
    }

    return schema ? { name, schema: { properties: { [name]: schema } } } : null;
  }

  get fileUploadFields() {
    const fileUploadFields = [];
    const properties = get(this.schema, 'formSchema.properties', {});
    Object.entries(properties).forEach(([key, value]) => {
      const uiControl = get(value, 'ui:control', null);
      if (uiControl === 'file-upload') {
        fileUploadFields.push(key);
      }
    });
    return fileUploadFields;
  }

  get formSchema() {
    return get(this.schema, 'formSchema', {});
  }

  get applicationType() {
    return get(this.schema, 'tclSchema.extras.applicationType', '');
  }

  get formType() {
    return get(this.schema, 'tclSchema.extras.formType', '');
  }

  get initialValues() {
    const values = {};
    const fromCookies = get(this.schema, 'tclSchema.extras.fromCookies', {});

    Object.keys(fromCookies).forEach((cookieKey) => {
      const cookieName = fromCookies[cookieKey];
      const cookieValue = cookies.get(cookieName);
      if (cookieValue !== undefined) {
        values[cookieKey] = cookieValue;
      }
    });
    return values;
  }

  get multiStep() {
    const steps = get(this.schema, 'tclSchema.extras.multiStep', []);
    return steps.map((step, index) => ({ index, ...step }));
  }

  get profileLinkFields() {
    const profileLinkFields = [];
    const properties = get(this.schema, 'formSchema.properties', {});
    Object.entries(properties).forEach(([key, value]) => {
      const uiControl = get(value, 'ui:control', null);
      if (uiControl === 'profile-links') {
        profileLinkFields.push(key);
      }
    });
    return profileLinkFields;
  }

  get submitLabel() {
    return get(this.schema, 'actionSchema.label');
  }

  sanitizeSchema(formSchema) {
    const handleSpecialTypes = ([name, property]) => {
      const propertyType = get(property, 'type', null);
      const propertyFormat = get(property, 'format', null);

      // Handle date-time formats.
      if (propertyType === 'string' && propertyFormat === 'date-time') {
        // eslint-disable-next-line no-param-reassign
        property.type = 'object';
        // eslint-disable-next-line no-param-reassign
        property['ui:control'] = 'localized-date';
      }

      // Handle email formats.
      if (
        propertyType === 'string' &&
        propertyFormat === 'email' &&
        isString(property.errorMessage)
      ) {
        // eslint-disable-next-line no-param-reassign
        property.errorMessage = {
          invalidEmail: get(property, 'errorMessage', 'Invalid email address'),
          internalEmail: get(
            property,
            'internalEmailErrorMessage',
            'Apply through Tesla Internal Jobs if you are an employee'
          ),
        };
      }

      // Handle countries.
      if (name === get(this.schema, 'tclSchema.extras.country', 'country')) {
        // eslint-disable-next-line no-param-reassign
        property['ui:control'] = 'country';
      }

      return property;
    };

    // Normal elements.
    const properties = get(formSchema, 'properties', {});
    Object.entries(properties).forEach(handleSpecialTypes);
    // Conditional elements.
    const allOf = get(formSchema, 'allOf', []);
    allOf.forEach((condition) => {
      const conditionalProperties = get(condition, 'then.properties', null);
      if (conditionalProperties) {
        Object.entries(conditionalProperties).forEach(handleSpecialTypes);
      } else {
        const conditionProperties = get(condition, 'properties', null);
        Object.entries(conditionProperties).forEach(handleSpecialTypes);
      }
    });

    formSchema.required.forEach((requiredField) => {
      if (formSchema?.properties?.[requiredField]) {
        // eslint-disable-next-line
        formSchema.properties[requiredField] = {
          ...formSchema.properties[requiredField],
          'ui:props': {
            ...formSchema.properties[requiredField]?.['ui:props'],
            required: true,
          },
        };
      }
    });

    // eslint-disable-next-line
    formSchema.errorMessage = {
      _: formSchema.errorMessage.required._,
      required: formSchema.errorMessage.required._,
    };
  }
}

export default TCLFormHelper;
